import React from 'react';
import type { VehicleSearchFilters, SelectFilterOption } from '../../types/manualsearch.types';
import { Button, Select, Label, SearchableSelect } from '@shared/components';
import { Search } from 'lucide-react';

interface ManualVehicleSearchProps {
    isLoading: boolean;
    isSearchLoading: boolean;
    message: string;
    derivativeOptions: {
        makes: SelectFilterOption[];
        models: SelectFilterOption[];
        engine_sizes: SelectFilterOption[];
        transmissions: SelectFilterOption[];
        fuel_types: SelectFilterOption[];
        years: SelectFilterOption[];
    };
    currentSelections: VehicleSearchFilters;
    onFilterChange: (filters: VehicleSearchFilters) => void;
    onSearch: (filters: VehicleSearchFilters) => void;
}

export const ManualVehicleSearch: React.FC<ManualVehicleSearchProps> = ({
    isLoading,
    isSearchLoading,
    message,
    derivativeOptions,
    currentSelections,
    onFilterChange,
    onSearch,
}) => {
    // Helper function to determine if a dropdown should be disabled
    const isDropdownDisabled = (dropdownType: string): boolean => {
        switch (dropdownType) {
            case 'model':
                return !currentSelections.make_id;
            case 'engine_size':
            case 'transmission':
            case 'fuel_type':
            case 'year':
                return !currentSelections.make_id || !currentSelections.model_id;
            default:
                return false;
        }
    };

    // Helper function to get current selection value
    const getSelectedValue = (filterKey: keyof VehicleSearchFilters): string => {
        const value = currentSelections ? currentSelections[filterKey] : undefined;
        return value ? value.toString() : '';
    };

    const handleFilterChange = (filterKey: keyof VehicleSearchFilters, value: string) => {
        const newFilters = { ...currentSelections };

        if (value === '') {
            delete newFilters[filterKey];
        } else {
            newFilters[filterKey] = value;
        }

        onFilterChange(newFilters);
    };

    const handleSearch = () => {
        onSearch(currentSelections);
    };

    // Memoise the selected-option lookups so the make/model comboboxes do not
    // rescan their (potentially large) option lists on every render.
    const makeValue = React.useMemo(
        () => derivativeOptions.makes.find((o) => o.value === currentSelections.make_id) ?? null,
        [derivativeOptions.makes, currentSelections.make_id],
    );
    const modelValue = React.useMemo(
        () => derivativeOptions.models.find((o) => o.value === currentSelections.model_id) ?? null,
        [derivativeOptions.models, currentSelections.model_id],
    );

    return (
        <div className="rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6">
            <div className="space-y-4 mb-6">
                <h3 className="text-lg font-semibold text-mp-foreground">
                    Manual Search by Make and Model
                </h3>
                <p className="text-sm text-mp-muted">
                    {message
                        ? message
                        : 'Search for a vehicle by selecting its make and model, along with other details.'}
                </p>
            </div>

            {/* Filter Grid */}
            <div className="space-y-4">
                {/* Make Selection (type-to-filter) */}
                <div>
                    <Label htmlFor="make">Make</Label>
                    <SearchableSelect<SelectFilterOption>
                        id="make"
                        options={derivativeOptions.makes}
                        value={makeValue}
                        onChange={(o) => handleFilterChange('make_id', o?.value ?? '')}
                        getOptionValue={(o) => o.value}
                        getOptionLabel={(o) => o.label}
                        disabled={derivativeOptions.makes.length === 0}
                        clearable
                        placeholder="Select a Make"
                        emptyLabel="No makes available."
                    />
                </div>

                {/* Model Selection (type-to-filter) */}
                <div>
                    <Label htmlFor="model">Model</Label>
                    <SearchableSelect<SelectFilterOption>
                        id="model"
                        options={derivativeOptions.models}
                        value={modelValue}
                        onChange={(o) => handleFilterChange('model_id', o?.value ?? '')}
                        getOptionValue={(o) => o.value}
                        getOptionLabel={(o) => o.label}
                        disabled={isDropdownDisabled('model')}
                        clearable
                        placeholder={!currentSelections.make_id ? 'Select a make first' : 'Select a Model'}
                        emptyLabel="No models available."
                    />
                </div>

                {/* Engine Size Selection */}
                <div>
                    <Label htmlFor="engine_size">Engine Size</Label>
                    <Select
                        id="engine_size"
                        options={derivativeOptions['engine_sizes']}
                        placeholder={
                            !currentSelections.model_id
                                ? 'Select a make and model first'
                                : 'Select Engine Size'
                        }
                        value={getSelectedValue('engine_size_id')}
                        onChange={(value) => handleFilterChange('engine_size_id', value)}
                        disabled={isDropdownDisabled('engine_size')}
                        isLoading={
                            isLoading && currentSelections.make_id && currentSelections.model_id
                                ? true
                                : false
                        }
                    />
                </div>

                {/* Transmission Selection */}
                <div>
                    <Label htmlFor="transmission">Transmission</Label>
                    <Select
                        id="transmission"
                        options={derivativeOptions['transmissions']}
                        placeholder={
                            !currentSelections.model_id
                                ? 'Select a make and model first'
                                : 'Select Transmission'
                        }
                        value={getSelectedValue('transmission_id')}
                        onChange={(value) => handleFilterChange('transmission_id', value)}
                        disabled={isDropdownDisabled('transmission')}
                        isLoading={
                            isLoading && currentSelections.make_id && currentSelections.model_id
                                ? true
                                : false
                        }
                    />
                </div>

                {/* Fuel Type Selection */}
                <div>
                    <Label htmlFor="fuel_type">Fuel Type</Label>
                    <Select
                        id="fuel_type"
                        options={derivativeOptions['fuel_types']}
                        placeholder={
                            !currentSelections.model_id
                                ? 'Select a make and model first'
                                : 'Select Fuel Type'
                        }
                        value={getSelectedValue('fuel_type_id')}
                        onChange={(value) => handleFilterChange('fuel_type_id', value)}
                        disabled={isDropdownDisabled('fuel_type')}
                        isLoading={
                            isLoading && currentSelections.make_id && currentSelections.model_id
                                ? true
                                : false
                        }
                    />
                </div>

                {/* Year Selection */}
                <div>
                    <Label htmlFor="year">Year</Label>
                    <Select
                        id="year"
                        options={derivativeOptions['years']}
                        placeholder={
                            !currentSelections.model_id
                                ? 'Select a make and model first'
                                : 'Select Year'
                        }
                        value={getSelectedValue('year')}
                        onChange={(value) => handleFilterChange('year', value)}
                        disabled={isDropdownDisabled('year')}
                        isLoading={
                            isLoading && currentSelections.make_id && currentSelections.model_id
                                ? true
                                : false
                        }
                    />
                </div>
            </div>

            {/* Search Button */}

            <div className="flex justify-end">
                <Button
                    onClick={handleSearch}
                    disabled={
                        !(currentSelections.make_id && currentSelections.model_id) ||
                        isLoading ||
                        isSearchLoading
                    }
                    startIcon={<Search size={18} />}
                    className="w-full"
                    size="sm"
                >
                    {isSearchLoading ? 'Searching...' : 'Search'}
                </Button>
            </div>
        </div>
    );
};
